import { useCallback, useEffect, useState } from 'react';
import {
  BreadcrumbProps,
  Breadcrumbs,
  Button,
  H2,
  Intent,
} from '@blueprintjs/core';
import { WavelightWorkflowState } from '../types';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
  resetState,
  setMetaData,
  uploadData,
  setSlowUploadAlert,
} from '../reducer';
import Loading from '../../../components/Loading';
import {
  WorkflowBreadcrumbsContainer,
  WorkflowSuccessContainer,
  WorkflowHeaderContainer,
  WorkflowErrorContainer,
} from './styles';
import PersonData from '../../../components/PersonData';
import Measurements from '../../../components/Measurements';
import Study from '../../../components/Study';
import Uploader from '../../../components/Uploader';
import SlowUploadAlert from '../../../components/SlowUploadAlert';
import BugsnagPerformance from '@bugsnag/browser-performance';

const Workflow = () => {
  const span = BugsnagPerformance.startSpan('Workflow');
  const dispatch = useAppDispatch();
  const state: WavelightWorkflowState = useAppSelector(
    (state) => state.workflow,
  );

  const [showSlowUploadAlert, setShowSlowUploadAlert] = useState(false);

  useEffect(() => {
    dispatch(setMetaData());
  }, [dispatch]);

  useEffect(() => {
    setShowSlowUploadAlert(state.slowUploadAlert);
  }, [state.slowUploadAlert]);

  const workflowSteps: BreadcrumbProps[] = [
    { text: '1. Dispositivos', current: state.currentStepIndex === 0 },
    { text: '2. Paciente', current: state.currentStepIndex === 1 },
    { text: '3. Medidas', current: state.currentStepIndex === 2 },
  ];

  const renderStep = useCallback(() => {
    if (state.currentStepIndex === 0) return <Study />;
    if (state.currentStepIndex === 1) return <PersonData />;
    if (state.currentStepIndex === 2) return <Measurements />;
  }, [state.currentStepIndex]);

  const resetCurrentState = useCallback(() => {
    dispatch(resetState());
  }, [dispatch]);

  const resendData = useCallback(() => {
    dispatch(uploadData());
  }, [dispatch]);

  const handleSlowUploadAlertClose = useCallback(() => {
    dispatch(setSlowUploadAlert(false));
  }, [dispatch]);

  if (state.dataUploadCompleted) {
    span.end();
    return (
      <WorkflowSuccessContainer>
        <H2>Vídeo enviado com sucesso!</H2>
        <Button
          fill
          icon={'reset'}
          intent={Intent.PRIMARY}
          onClick={resetCurrentState}
        >
          Enviar outro vídeo
        </Button>
      </WorkflowSuccessContainer>
    );
  }

  if (state.isLoading) {
    return <Loading message="Carregando" />;
  }

  if (state.dataUploadError) {
    return (
      <WorkflowErrorContainer>
        <H2>Ocorreu um erro ao enviar os dados</H2>
        <Button fill icon={'reset'} intent={Intent.DANGER} onClick={resendData}>
          Enviar novamente
        </Button>
      </WorkflowErrorContainer>
    );
  }

  return (
    <>
      <WorkflowHeaderContainer $videoUploadError={state.videoUploadError}>
        <Uploader />
        <WorkflowBreadcrumbsContainer>
          <Breadcrumbs items={workflowSteps} />
        </WorkflowBreadcrumbsContainer>
      </WorkflowHeaderContainer>
      {renderStep()}
      {/* Renderizar o alerta de upload lento */}
      <SlowUploadAlert
        isOpen={showSlowUploadAlert}
        onClose={handleSlowUploadAlertClose}
      />
    </>
  );
};

export default Workflow;
